import BaseStore from "../base";
import { observable, computed, action } from "mobx";
import format from "@app/lib/format";
import _ from "lodash";
import { ReportTypes } from "@app/constants";

import report from "@app/state/store/report";

export class DocumentStore extends BaseStore {
    sections = [];
    sectionMap = {};
    citationData = {};
    refCount = {};

    observable() {
        return {
            documentId: null,
            figureMap: {},
            tableMap: {},
            refMap: {},
            figures: [],
            tables: [],
            refs: [],
            reports: {},
            highlight: "yellow",
            loaded: false,
            sectionsVersion: 0,
        };
    }

    get globalIndex() {
        return report.config?.reportDocuments?.style?.captionIndex === "global";
    }

    /**
     * Update the current active document
     */
    @action
    init(documentId, sections, forceUpdate = false) {
        if (this.documentId === documentId && !forceUpdate) {
            return;
        }

        this.documentId = documentId;
        this.sections = [];
        this.figureMap = {};
        this.tableMap = {};
        this.refMap = {};
        this.figures = [];
        this.tables = [];
        this.refs = [];
        this.refCount = {};
        this.reports = {};

        const styleConfig = report.config?.reportDocuments?.style ?? {};
        const withDots = styleConfig.sectionNumbering === "withDots";

        // process the sections
        let prefix = 1;
        for (const section of sections) {
            // update the top level prefix
            if (!section.parent) {
                const match = section.displayPos.match(/^(\d+)\./);
                if (match) {
                    prefix = match[1];
                }
            }

            const titlePrefix = section.displayPos.replace(/\.$/, "");
            const fullTitle = withDots
                ? `${titlePrefix}. ${section.title}`
                : `${titlePrefix} ${section.title}`;

            const data = {
                _id: section._id,
                title: section.title,
                prefix: prefix,
                number: section.displayPos,
                fullTitle,
                figures: [],
                tables: [],
                refs: [],
            };

            this.sections.push(data);
            this.sectionMap[data._id] = data;

            this.loaded = true;
        }
    }

    /**
     * Process a section content
     */
    @action
    processSection(sectionId, data) {
        if (!sectionId) {
            return;
        }

        const section = this.sectionMap[sectionId];
        if (!section) {
            return;
        }

        const nodes = this.flatten(data?.content ?? []);
        const prefix = this.globalIndex ? "" : section.prefix;

        // find all figures
        const figures = nodes
            .filter((node) => {
                if (!node.attrs?.error && node.attrs?.caption) {
                    if ("image" === node.type || "image" === node.attrs?.dataType) {
                        return true;
                    }
                }
                return false;
            })
            .map((figure) => {
                const { id, caption } = figure.attrs;

                const entry = new FigureEntry();
                entry.id = id;
                entry.caption = caption;
                entry.sectionPrefix = prefix;

                this.figureMap[id] = entry;
                return entry;
            });

        // find all tables (excluding nested tables from the numbered list)
        const tables = nodes
            .filter((node) => {
                if (!node.attrs?.error && node.attrs?.caption) {
                    if ("table" === node.type || "table" === node.attrs?.dataType) {
                        // Exclude nested tables from the numbered table list
                        return !node._isNested;
                    }
                }
                return false;
            })
            .map((table) => {
                const { id, caption } = table.attrs;

                const entry = new TableEntry();
                entry.id = id;
                entry.caption = caption;
                entry.sectionPrefix = prefix;
                entry.isNested = false;

                this.tableMap[id] = entry;
                return entry;
            });

        // Also track nested tables in the tableMap but mark them as nested
        nodes
            .filter((node) => {
                if (!node.attrs?.error) {
                    if ("table" === node.type || "table" === node.attrs?.dataType) {
                        return node._isNested;
                    }
                }
                return false;
            })
            .forEach((table) => {
                const { id, caption } = table.attrs;

                const entry = new TableEntry();
                entry.id = id;
                entry.caption = caption || "";
                entry.sectionPrefix = prefix;
                entry.isNested = true;

                this.tableMap[id] = entry;
            });

        // find all references
        const refs = nodes
            .filter((node) => node.type === "citation")
            .map((ref) => {
                const { id } = ref.attrs;
                let entry = this.refMap[id];

                if (!entry) {
                    entry = new ReferenceEntry();
                    entry.id = id;
                    this.refMap[id] = entry;
                }

                // Always update data from citationData
                entry.data = this.citationData[id];

                return entry;
            });

        // find all reports
        nodes
            .filter((node) => node.type === "report")
            .forEach((node) => {
                // only create a default when it doesn't exist
                if (!this.reports[node.attrs.id]) {
                    if (node.attrs.reportType === ReportTypes.APPENDIX) {
                        this.reports[node.attrs.id] = { rows: 1, loadAll: false };
                    } else {
                        this.reports[node.attrs.id] = { rows: 20 };
                    }
                }
            });

        // save section tables and figures
        section.figures = figures;
        section.tables = tables;
        section.refs = refs;

        // update the captions
        this.updateFigures();
        this.updateTables();
        this.updateRefs();
    }

    /**
     * Update the figure captions
     */
    @action
    updateFigures() {
        let count = {};
        let list = [];
        let global = 1;

        // update numbering
        for (const section of this.sections) {
            if (!count[section.prefix]) {
                count[section.prefix] = 1;
            }

            for (const figure of section.figures) {
                if (this.globalIndex) {
                    figure.index = global++;
                } else {
                    figure.index = count[section.prefix]++;
                }
            }

            list.push(...section.figures);
        }

        if (!_.isEqual(this.figures, list)) {
            this.figures = list;
        }
    }

    /**
     * Update the table captions
     */
    @action
    updateTables() {
        let count = {};
        let list = [];
        let global = 1;

        for (const section of this.sections) {
            if (!count[section.prefix]) {
                count[section.prefix] = 1;
            }

            for (const table of section.tables) {
                if (this.globalIndex) {
                    table.index = global++;
                } else {
                    table.index = count[section.prefix]++;
                }
            }

            list.push(...section.tables);
        }

        if (!_.isEqual(this.tables, list)) {
            this.tables = list;
        }
    }

    /**
     * Update the references
     */
    @action
    updateRefs() {
        let list = [];
        let used = {};
        let index = 1;

        for (const section of this.sections) {
            for (const ref of section.refs) {
                if (!used[ref.id]) {
                    list.push(ref);
                    used[ref.id] = true;

                    // Ensure ref.data is set from citationData if available
                    if (!ref.data && this.citationData[ref.id]) {
                        ref.data = this.citationData[ref.id];
                    }

                    if (ref.isValid) {
                        ref.index = index++;
                    }
                }
            }
        }

        if (!_.isEqual(this.refs, list)) {
            this.refs = list;
        }
    }

    /**
     * Flattens a tree of nodes with nesting context
     * Marks nodes that are inside table cells as nested
     */
    flatten(tree) {
        const flattenWithContext = (nodes, insideTableCell = false) => {
            const result = [];

            for (const node of nodes) {
                // Mark the node with its nesting context
                const nodeWithContext = {
                    ...node,
                    _isNested: insideTableCell,
                };
                result.push(nodeWithContext);

                if (node.content?.length) {
                    // Check if this node is a table cell (content will be nested)
                    const isTableCell = node.type === "tableCell" || node.type === "tableHeader";
                    const childNodes = flattenWithContext(
                        node.content,
                        insideTableCell || isTableCell,
                    );
                    result.push(...childNodes);
                }
            }

            return result;
        };

        return flattenWithContext(tree, false);
    }

    /**
     * Return the figure details by id
     */
    figureDetails(id) {
        return this.figureMap[id];
    }

    /**
     * Return the table details by id
     */
    tableDetails(id) {
        return this.tableMap[id];
    }

    /**
     * Update a figure caption
     */
    updateFigureCaption(id, caption) {
        const figure = this.figureMap[id];
        if (figure) {
            figure.caption = caption;
        }
    }

    /**
     * Update a table caption
     */
    updateTableCaption(id, caption) {
        const table = this.tableMap[id];
        if (table) {
            table.caption = caption;
        }
    }

    /**
     * Update citation data
     */
    @action
    setCitations(data) {
        this.citationData = data;

        // Update all refs in refMap
        Object.values(this.refMap).forEach((ref) => {
            const entry = data[ref.id];
            if (entry) {
                ref.data = entry;
            }
        });

        // Also update refs in all section.refs arrays
        for (const section of this.sections) {
            for (const ref of section.refs || []) {
                const entry = data[ref.id];
                if (entry && !ref.data) {
                    ref.data = entry;
                }
            }
        }

        this.updateRefs();
    }

    /**
     * Update section order when TOC reorders sections
     * This preserves refs, figures, and tables data while updating numbering
     */
    @action
    updateSectionOrder(sections) {
        const styleConfig = report.config?.reportDocuments?.style ?? {};
        const withDots = styleConfig.sectionNumbering === "withDots";

        // Create a map of old section data (refs, figures, tables)
        const oldData = {};
        for (const section of this.sections) {
            oldData[section._id] = {
                figures: section.figures,
                tables: section.tables,
                refs: section.refs,
            };
        }

        // Rebuild sections array in new order
        this.sections = [];

        for (const section of sections) {
            // Extract prefix from displayPos (first number, e.g., "2.1." -> 2)
            const match = section.displayPos?.match(/^(\d+)\./);
            const prefix = match ? parseInt(match[1], 10) : 1;

            const titlePrefix = (section.displayPos || "").replace(/\.$/, "");
            const fullTitle = withDots
                ? `${titlePrefix}. ${section.title}`
                : `${titlePrefix} ${section.title}`;

            const existing = this.sectionMap[section._id];
            const figures = oldData[section._id]?.figures || existing?.figures || [];
            const tables = oldData[section._id]?.tables || existing?.tables || [];

            const sectionPrefix = this.globalIndex ? "" : prefix;
            figures.forEach((f) => {
                f.sectionPrefix = sectionPrefix;
            });
            tables.forEach((t) => {
                t.sectionPrefix = sectionPrefix;
            });

            const data = {
                _id: section._id,
                title: section.title,
                prefix: prefix,
                number: section.displayPos,
                fullTitle,
                figures,
                tables,
                refs: oldData[section._id]?.refs || existing?.refs || [],
            };

            this.sections.push(data);
            this.sectionMap[data._id] = data;
        }

        // Increment version to trigger reactivity in CrossReferenceStore
        this.sectionsVersion++;

        this.updateFigures();
        this.updateTables();
        this.updateRefs();

        // Force new array references so TableView reaction, Appendix observer,
        // and CrossReferenceStore detect the change and update in real-time
        this.figures = [...this.figures];
        this.tables = [...this.tables];
    }

    /**
     * Update a section's title
     */
    @action
    updateSectionTitle(sectionId, newTitle) {
        const section = this.sectionMap[sectionId];
        if (!section) {
            return;
        }

        const styleConfig = report.config?.reportDocuments?.style ?? {};
        const withDots = styleConfig.sectionNumbering === "withDots";
        const titlePrefix = section.number.replace(/\.$/, "");
        const fullTitle = withDots ? `${titlePrefix}. ${newTitle}` : `${titlePrefix} ${newTitle}`;

        section.title = newTitle;
        section.fullTitle = fullTitle;

        const observableSection = this.sections.find((s) => s._id === sectionId);
        if (observableSection) {
            observableSection.title = newTitle;
            observableSection.fullTitle = fullTitle;
        }

        this.sectionsVersion++;
    }
}

class FigureEntry {
    id = undefined;
    @observable sectionPrefix = "";

    @observable index = "";
    @observable caption = "";

    @computed get prefix() {
        if (this.sectionPrefix) {
            return `Figure ${this.sectionPrefix}-${this.index}`;
        }

        return `Figure ${this.index}`;
    }

    @computed get fullCaption() {
        return `${this.prefix}: ${format.caption(this.caption)}`;
    }
}

class TableEntry {
    id = undefined;
    @observable sectionPrefix = "";
    isNested = false;

    @observable index = "";
    @observable caption = "";

    @computed get prefix() {
        // Nested tables don't have a prefix
        if (this.isNested) {
            return "";
        }

        if (this.sectionPrefix) {
            return `Table ${this.sectionPrefix}-${this.index}`;
        }

        return `Table ${this.index}`;
    }

    @computed get fullCaption() {
        // Nested tables don't display captions
        if (this.isNested) {
            return "";
        }

        return `${this.prefix}: ${format.caption(this.caption)}`;
    }
}

class ReferenceEntry {
    id = undefined;
    @observable index = 0;
    @observable data = undefined;

    @computed get isValid() {
        return !!this.data;
    }

    @computed get file() {
        return this.data?.file;
    }

    @computed get formatted() {
        if (!this.data) {
            return "";
        }

        const refStyle = report?.config?.reference?.style;
        return `${this.index}. ${format.reference(this.data, refStyle)}`;
    }
}

export default new DocumentStore();
